import React, { useState, useMemo } from 'react';
import {
  HiOutlineRefresh, HiOutlineChevronDown, HiOutlineClock, HiOutlineFilter,
} from 'react-icons/hi';
import { toast } from 'react-toastify';
import { SEVERITY_LEVELS, TIME_PRESETS, toLocalDatetimeString, getSeverityClass, safeISOString } from '../shared/constants';
import LogKV from '../shared/LogKV';

function getDefaultResourceName(log) {
  return log.resource?.labels?.service_name
    || log.resource?.labels?.job_name
    || '-';
}

export default function LogViewer({
  logs,
  logsLoading,
  fetchLogs,
  logTimePreset,
  setLogTimePreset,
  logStartTime,
  setLogStartTime,
  logEndTime,
  setLogEndTime,
  logSeverityFilter,
  setLogSeverityFilter,
  showLogTimePresets,
  setShowLogTimePresets,
  showLogSeverityDropdown,
  setShowLogSeverityDropdown,
  logsPageSize,
  setLogsPageSize,
  logsCurrentPage,
  setLogsCurrentPage,
  resourceColumnLabel = 'Resource',
  getResourceName = getDefaultResourceName,
}) {
  const [sortByResource, setSortByResource] = useState(false);

  const sortedLogs = useMemo(() => {
    if (!sortByResource) return logs;
    return [...logs].sort((a, b) =>
      getResourceName(a).localeCompare(getResourceName(b))
    );
  }, [logs, sortByResource, getResourceName]);

  const totalPages = Math.max(1, Math.ceil(sortedLogs.length / logsPageSize));
  const startIdx = (logsCurrentPage - 1) * logsPageSize;
  const paginatedLogs = sortedLogs.slice(startIdx, startIdx + logsPageSize);
  const activeLogPresetLabel = TIME_PRESETS.find(p => p.value === logTimePreset)?.label || 'Select time';

  const toggleSeverity = (sev) => {
    setLogSeverityFilter(prev => prev.includes(sev) ? prev.filter(s => s !== sev) : [...prev, sev]);
  };

  return (
    <div className="logs-section">
      <div className="filter-toolbar">
        <div className="filter-group" onClick={(e) => e.stopPropagation()}>
          <div className="dropdown-trigger" onClick={() => { setShowLogTimePresets(!showLogTimePresets); setShowLogSeverityDropdown(false); }}>
            <HiOutlineClock size={15} />
            <span>{activeLogPresetLabel}</span>
            <HiOutlineChevronDown size={14} />
          </div>
          {showLogTimePresets && (
            <div className="dropdown-menu time-dropdown">
              {TIME_PRESETS.map(p => (
                <div
                  key={p.value}
                  className={`dropdown-item ${logTimePreset === p.value ? 'active' : ''}`}
                  onClick={() => { setLogTimePreset(p.value); setShowLogTimePresets(false); }}
                >
                  {p.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="filter-group" onClick={(e) => e.stopPropagation()}>
          <div className="dropdown-trigger" onClick={() => { setShowLogSeverityDropdown(!showLogSeverityDropdown); setShowLogTimePresets(false); }}>
            <HiOutlineFilter size={15} />
            <span>{logSeverityFilter.length === 0 ? 'All severities' : `${logSeverityFilter.length} selected`}</span>
            <HiOutlineChevronDown size={14} />
          </div>
          {showLogSeverityDropdown && (
            <div className="dropdown-menu severity-dropdown">
              <div className="dropdown-header">
                <span>Filter by severity</span>
                {logSeverityFilter.length > 0 && (
                  <button className="clear-filter-btn" type="button" onClick={() => setLogSeverityFilter([])}>Clear</button>
                )}
              </div>
              {SEVERITY_LEVELS.map(sev => (
                <label key={sev} className={`dropdown-item checkbox-item ${logSeverityFilter.includes(sev) ? 'active' : ''}`}>
                  <input type="checkbox" checked={logSeverityFilter.includes(sev)} onChange={() => toggleSeverity(sev)} />
                  <span className={`severity-dot ${getSeverityClass(sev)}`}></span>
                  <span>{sev}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {logSeverityFilter.length > 0 && (
          <div className="active-filters">
            {logSeverityFilter.map(sev => (
              <span key={sev} className={`filter-chip ${getSeverityClass(sev)}`}>
                {sev}<button type="button" onClick={() => toggleSeverity(sev)}>&times;</button>
              </span>
            ))}
          </div>
        )}

        <div className="filter-spacer"></div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => fetchLogs()}><HiOutlineRefresh size={14} /></button>
        <span className="log-count">{logs.length} entries</span>
      </div>

      {logTimePreset === 'custom' && (
        <div className="custom-time-range">
          <div className="time-input-group">
            <label>Start Time</label>
            <input
              type="datetime-local"
              value={logStartTime ? toLocalDatetimeString(logStartTime) : ''}
              onChange={(e) => setLogStartTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
              max={toLocalDatetimeString(new Date().toISOString())}
            />
          </div>
          <div className="time-input-group">
            <label>End Time</label>
            <input
              type="datetime-local"
              value={logEndTime ? toLocalDatetimeString(logEndTime) : ''}
              onChange={(e) => setLogEndTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
              max={toLocalDatetimeString(new Date().toISOString())}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => { if (logStartTime && logEndTime) fetchLogs(); else toast.error('Both times required'); }}
          >
            Apply
          </button>
        </div>
      )}

      {logsLoading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : logs.length === 0 ? (
        <div className="empty-state"><h3>No logs found</h3><p>No log entries match the selected filters</p></div>
      ) : (
        <div className="logs-table">
          <div className="logs-table-header">
            <div className="log-col-severity">Severity</div>
            <div className="log-col-timestamp">Timestamp</div>
            <div
              className={`log-col-service sortable-col ${sortByResource ? 'sort-active' : ''}`}
              onClick={() => { setSortByResource(v => !v); setLogsCurrentPage(1); }}
            >
              {resourceColumnLabel} <span className="sort-arrow">{sortByResource ? '▲' : '▼'}</span>
            </div>
            <div className="log-col-content">Summary</div>
          </div>
          <div className="logs-pagination">
            <div className="logs-pagination-left">
              <span className="logs-total">
                Showing {startIdx + 1}–{Math.min(startIdx + logsPageSize, sortedLogs.length)} of {sortedLogs.length} logs
              </span>
            </div>
            <div className="logs-pagination-center">
              <button type="button" className="page-btn" disabled={logsCurrentPage <= 1} onClick={() => setLogsCurrentPage(1)} title="First page">&laquo;</button>
              <button type="button" className="page-btn" disabled={logsCurrentPage <= 1} onClick={() => setLogsCurrentPage(p => p - 1)}>&lsaquo;</button>
              <span className="page-info">Page {logsCurrentPage} of {totalPages}</span>
              <button type="button" className="page-btn" disabled={logsCurrentPage >= totalPages} onClick={() => setLogsCurrentPage(p => p + 1)}>&rsaquo;</button>
              <button type="button" className="page-btn" disabled={logsCurrentPage >= totalPages} onClick={() => setLogsCurrentPage(totalPages)} title="Last page">&raquo;</button>
            </div>
            <div className="logs-pagination-right">
              <span className="page-size-label">Per page</span>
              {[50, 100, 150, 200].map(size => (
                <button
                  key={size}
                  type="button"
                  className={`page-size-btn ${logsPageSize === size ? 'active' : ''}`}
                  onClick={() => { setLogsPageSize(size); setLogsCurrentPage(1); }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          <div className="logs-table-body">
            {paginatedLogs.map((log, i) => (
              <LogRow key={`${log.insertId}-${startIdx + i}`} log={log} getResourceName={getResourceName} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LogRow({ log, getResourceName }) {
  const [expanded, setExpanded] = useState(false);
  const content = log.textPayload || (log.jsonPayload ? JSON.stringify(log.jsonPayload) : '');
  const shortContent = content.length > 200 ? content.slice(0, 200) + '...' : content;
  const resourceName = getResourceName(log);

  const hasResourceLabels = log.resource?.labels && Object.keys(log.resource.labels).length > 0;
  const hasLabels = log.labels && Object.keys(log.labels).length > 0;
  const hasHttp = log.httpRequest && (log.httpRequest.requestMethod || log.httpRequest.requestUrl);
  const hasOperation = log.operation && (log.operation.id || log.operation.producer);
  const hasSource = log.sourceLocation && (log.sourceLocation.file || log.sourceLocation.function);

  return (
    <div className={`log-row ${getSeverityClass(log.severity)} ${expanded ? 'expanded' : ''}`}>
      <div className="log-row-main" onClick={() => setExpanded(!expanded)}>
        <div className="log-col-chevron"><span className={`log-chevron ${expanded ? 'open' : ''}`}>&#9654;</span></div>
        <div className="log-col-severity"><span className={`log-severity-badge ${getSeverityClass(log.severity)}`}>{log.severity}</span></div>
        <div className="log-col-timestamp">{new Date(log.timestamp).toLocaleString()}</div>
        <div className="log-col-service"><span>{resourceName}</span></div>
        <div className="log-col-content">{expanded ? content : shortContent}</div>
      </div>
      {expanded && (
        <div className="log-row-expanded" onClick={e => e.stopPropagation()}>
          {log.jsonPayload && (
            <div className="log-exp-section">
              <div className="log-exp-title">JSON Payload</div>
              <div className="log-json-view"><pre>{JSON.stringify(log.jsonPayload, null, 2)}</pre></div>
            </div>
          )}
          {log.textPayload && (
            <div className="log-exp-section">
              <div className="log-exp-title">Text Payload</div>
              <div className="log-text-view">{log.textPayload}</div>
            </div>
          )}
          <div className="log-exp-section">
            <div className="log-exp-title">Log Entry</div>
            <div className="log-kv-table">
              <LogKV label="Insert ID" value={log.insertId} />
              <LogKV label="Log Name" value={log.logName} />
              <LogKV label="Severity" value={log.severity} />
              <LogKV label="Timestamp" value={safeISOString(log.timestamp)} />
              <LogKV label="Receive Timestamp" value={safeISOString(log.receiveTimestamp)} />
              <LogKV label="Trace" value={log.trace} />
              <LogKV label="Span ID" value={log.spanId} />
            </div>
          </div>
          {log.resource && (
            <div className="log-exp-section">
              <div className="log-exp-title">Resource</div>
              <div className="log-kv-table">
                <LogKV label="Type" value={log.resource.type} />
                {hasResourceLabels && Object.entries(log.resource.labels).map(([k, v]) => <LogKV key={k} label={k} value={v} />)}
              </div>
            </div>
          )}
          {hasLabels && (
            <div className="log-exp-section">
              <div className="log-exp-title">Labels</div>
              <div className="log-kv-table">
                {Object.entries(log.labels).map(([k, v]) => <LogKV key={k} label={k} value={v} />)}
              </div>
            </div>
          )}
          {hasHttp && (
            <div className="log-exp-section">
              <div className="log-exp-title">HTTP Request</div>
              <div className="log-kv-table">
                <LogKV label="Method" value={log.httpRequest.requestMethod} />
                <LogKV label="URL" value={log.httpRequest.requestUrl} />
                <LogKV label="Status" value={log.httpRequest.status} />
              </div>
            </div>
          )}
          {hasOperation && (
            <div className="log-exp-section">
              <div className="log-exp-title">Operation</div>
              <div className="log-kv-table">
                <LogKV label="ID" value={log.operation.id} />
                <LogKV label="Producer" value={log.operation.producer} />
              </div>
            </div>
          )}
          {hasSource && (
            <div className="log-exp-section">
              <div className="log-exp-title">Source Location</div>
              <div className="log-kv-table">
                <LogKV label="File" value={log.sourceLocation.file} />
                <LogKV label="Line" value={log.sourceLocation.line} />
                <LogKV label="Function" value={log.sourceLocation.function} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
